<template>
  <div class="order-detail-page">
    <div class="breadcrumb-bar">
      当前位置：<RouterLink to="/ticketList">车票订单</RouterLink>
      <span class="sep"> &gt; </span>
      <span>订单详情</span>
    </div>

    <Card v-if="state.loading" :bordered="false">
      <Spin tip="正在加载订单信息…" />
    </Card>

    <template v-else-if="state.order">
      <Tabs v-model:activeKey="state.tab" type="card" class="detail-tabs">
        <TabPane key="track" tab="订单状态">
          <div class="track-layout">
            <div class="order-side-card">
              <div class="side-title">订单信息</div>
              <div class="side-row">订票日期：{{ state.order.orderTime }}</div>
              <div class="side-row">订单号：{{ state.order.orderSn }}</div>
            </div>
            <div class="timeline-panel">
              <div v-for="(node, idx) in timelineNodes" :key="idx" class="tl-row">
                <div class="tl-axis">
                  <span class="tl-dot" />
                  <span v-if="idx < timelineNodes.length - 1" class="tl-line" />
                </div>
                <div class="tl-body">
                  <div class="node-date">{{ node.dateLabel }}</div>
                  <div class="node-time">{{ node.time }}</div>
                  <div class="node-title" :class="{ 'node-title-fail': node.success === false }">
                    {{ node.title }}
                  </div>
                  <div v-for="(line, i) in node.lines" :key="i" class="node-line">{{ line }}</div>
                </div>
              </div>
            </div>
          </div>
        </TabPane>
        <TabPane key="refund" tab="退款/退票">
          <Spin :spinning="refundsLoading" tip="正在加载退票记录…">
            <div v-if="refundBlocks.length" class="refund-list-container">
              <div v-for="(item, index) in refundBlocks" :key="index" class="refund-card">
                <Table
                  :dataSource="[item]"
                  :columns="refundColumns"
                  :pagination="false"
                  bordered
                  size="middle"
                  :showHeader="true"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'refundInfo'">
                      <div class="refund-info-cell">
                        <div class="refund-amount-row">
                          <span class="label">退款金额：</span>
                          <span class="amount-value">￥{{ record.amountYuan }}</span>
                        </div>
                        <div
                          v-if="record.railwayRefundFeeYuan && record.refundStatus !== 2"
                          class="refund-railway-fee-row"
                        >
                          <span class="label">收取退票费：</span>
                          <span class="fee-value">￥{{ record.railwayRefundFeeYuan }}</span>
                        </div>
                        <div
                          v-if="record.serviceFeeYuan && record.refundStatus !== 2"
                          class="refund-service-fee-row"
                        >
                          <span class="label">退票服务费：</span>
                          <span class="service-fee-value">￥{{ record.serviceFeeYuan }}</span>
                        </div>
                        <div class="refund-source-row">
                          <span class="label">来源：</span>
                          <span class="source-value">{{ record.source }}</span>
                        </div>
                        <div class="refund-time-row">
                          <span class="label">申请时间：</span>
                          <span class="time-value">{{ record.applyTime }}</span>
                        </div>
                      </div>
                    </template>
                    <template v-if="column.key === 'detail'">
                      <span class="detail-text">{{ record.detail }}</span>
                    </template>
                    <template v-if="column.key === 'serial'">
                      <span class="serial-text">{{ record.serial }}</span>
                    </template>
                    <template v-if="column.key === 'status'">
                      <span class="status-text">{{ record.statusText }}</span>
                    </template>
                  </template>
                </Table>
              </div>
            </div>
            <Empty v-else-if="!refundsLoading" description="暂无退款/退票记录" />
          </Spin>
          <Alert type="warning" show-icon class="warm-tip" message="温馨提示">
            <template #description>
              <p>
                1. 车票退票、改签或变更到站后，票款将按银行规定时限退还至原支付账户；若超过规定时间仍未收到退款，请致电12306客服或至车站窗口咨询。
              </p>
              <p>2. 退款到账时间以支付渠道及银行处理为准，如有疑问请按支付渠道指引查询。</p>
            </template>
          </Alert>
        </TabPane>
<!--        <TabPane key="insurance" tab="铁路乘意险">-->
<!--          <Alert-->
<!--            type="info"-->
<!--            show-icon-->
<!--            message="铁路乘意险的投保、退保及理赔由保险公司按规定办理，请在支付页面或订单详情中按提示操作。"-->
<!--          />-->
<!--        </TabPane>-->
<!--        <TabPane key="child" tab="免费乘车儿童">-->
<!--          <Alert-->
<!--            type="info"-->
<!--            show-icon-->
<!--            message="每一名持票成年人旅客可免费携带一名未满6周岁且不单独占用席位的儿童乘车；申报与核验规则以车站及12306.cn公告为准。"-->
<!--          />-->
<!--        </TabPane>-->
      </Tabs>
    </template>

    <Empty v-else description="未找到订单或无权查看" />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import {
  Card,
  Tabs,
  TabPane,
  Spin,
  Empty,
  Alert,
  Table
} from 'ant-design-vue'
import dayjs from 'dayjs'
import { fetchOrderBySn, fetchRefundByOrderSn, fetchOrderStatusLog } from '@/service'
import { getWeekNumber } from '@/utils'
import { TICKET_STATUS_LIST } from '@/constants'
import { formatTimelineSeatSnippet, enrichTimelineDetailLine, getRefundSerial } from '@/utils/orderDisplay'

/** 订单详情「退款/退票」明细文案（不含服务费） */
const buildRefundDetailText = (realName, refundStatus) => {
  const name = realName || '乘客'
  if (refundStatus === 2) {
    return `${name}退票失败`
  }
  if (refundStatus === 0) {
    return `${name}退票中`
  }
  return `${name}已退票`
}

const route = useRoute()
const state = reactive({
  loading: true,
  order: null,
  tab: 'track'
})
const refundsLoading = ref(false)
const refundsData = ref([])
const statusLogs = ref([])

const timelineNodes = computed(() => {
  const o = state.order
  if (!o) return []

  if (statusLogs.value?.length) {
    return [...statusLogs.value]
      .map((log) => {
        const eventTime = log.eventTime ? dayjs(log.eventTime) : dayjs()
        return {
          eventTs: eventTime.valueOf(),
          dateLabel: `${eventTime.format('YYYY-MM-DD')}/${getWeekNumber(eventTime.day())}`,
          time: eventTime.format('HH:mm:ss'),
          title: log.eventTitle || log.eventType || '—',
          lines: log.detailLines?.length
            ? log.detailLines
                .filter((line) => !String(line || '').startsWith('失败编码'))
                .map((line) => enrichTimelineDetailLine(line, o))
            : log.relatedOrderSn
              ? [`关联订单：${log.relatedOrderSn}`]
              : [],
          success: log.success !== false
        }
      })
      .sort((a, b) => b.eventTs - a.eventTs)
      .map(({ eventTs, ...node }) => node)
  }

  const list = []
  const baseDate = o.payTime
    ? dayjs(o.payTime)
    : o.orderTime
      ? dayjs(o.orderTime)
      : dayjs()
  const dateLabel = `${baseDate.format('YYYY-MM-DD')}/${getWeekNumber(baseDate.day())}`
  const time = baseDate.format('HH:mm:ss')
  const linesPaid =
    o.passengerDetails?.map((p) =>
      formatTimelineSeatSnippet(
        p,
        o.trainNumber,
        o.departure,
        o.arrival,
        o.ridingDate,
        o.departureTime
      )
    ) ?? []
  list.push({
    dateLabel,
    time,
    title: '已支付(互联网/手机端)',
    lines: linesPaid.length ? linesPaid : [`订单 ${o.orderSn} 已创建`]
  })
  return list
})

const refundBlocks = computed(() => {
  // 优先使用后端返回的退票数据
  if (refundsData.value && refundsData.value.length > 0) {
    return refundsData.value.map((item) => {
      // 格式化退款时间
      let applyTime = '—'
      if (item.refundTime) {
        applyTime = dayjs(item.refundTime).format('YYYY-MM-DD HH:mm:ss')
      }

      // 生成流水号
      const serial = item.tradeNo || item.orderSn ?
        `${item.tradeNo || item.orderSn}` : '—'

      const refundStatusLabel = {
        0: '退款处理中',
        1: '已退票',
        2: '退票失败'
      }
      const statusText = refundStatusLabel[item.status] ?? '退款处理中'

      const formatYuan = (val) =>
        val != null && !Number.isNaN(Number(val)) ? Number(val).toFixed(2) : null

      const ticketPriceYuan = formatYuan(item.ticketPrice)
      const railwayRefundFeeYuan = formatYuan(item.railwayRefundFee)
      const serviceFeeYuan = formatYuan(item.refundServiceFee)
      const refundAmountYuan = formatYuan(item.refundAmount) ?? '0.00'

      return {
        realName: item.realName || '—',
        amountYuan: refundAmountYuan,
        ticketPriceYuan,
        railwayRefundFeeYuan,
        serviceFeeYuan,
        refundStatus: item.status,
        source: `${statusText}`,
        applyTime: applyTime,
        detail: buildRefundDetailText(item.realName, item.status),
        serial: serial,
        statusText: statusText
      }
    })
  }

  // 兜底：从订单的passengerDetails中提取退票记录
  const o = state.order
  if (!o?.passengerDetails) return []
  const label40 = TICKET_STATUS_LIST.find((x) => x.value === 40)?.label ?? '已退票'
  const label41 = TICKET_STATUS_LIST.find((x) => x.value === 41)?.label ?? '退票中'
  return o.passengerDetails
    .filter((p) => p.status === 40 || p.status === 41)
    .map((p) => {
      const ticketPriceYuan =
        p.amount != null ? (Number(p.amount) / 100).toFixed(2) : null
      const railwayRefundFeeYuan =
        p.railwayRefundFee != null ? (Number(p.railwayRefundFee) / 100).toFixed(2) : null
      const serviceFeeYuan =
        p.refundFee != null ? (Number(p.refundFee) / 100).toFixed(2) : null
      const refundAmountYuan =
        p.ticketRefundAmount != null
          ? (Number(p.ticketRefundAmount) / 100).toFixed(2)
          : ticketPriceYuan ?? '0.00'
      const refundStatus = p.status === 41 ? 0 : 1
      return {
        realName: p.realName,
        amountYuan: refundAmountYuan,
        ticketPriceYuan,
        railwayRefundFeeYuan,
        serviceFeeYuan,
        refundStatus,
        source: p.status === 41 ? label41 : label40,
        applyTime: p.refundTime || o.orderTime || '—',
        detail: buildRefundDetailText(p.realName, refundStatus),
        serial: getRefundSerial({ ...p, orderSn: o.orderSn }),
        statusText:
          p.refundStatusText ||
          '成功，已成功退至银行，如已超过7个工作日尚未到账，请持支付银行卡至支付宝查询。'
      }
    })
})

const refundColumns = [
  {
    title: '退款信息',
    key: 'refundInfo',
    width: 280
  },
  {
    title: '明细',
    key: 'detail',
    width: 200
  },
  {
    title: '流水号',
    key: 'serial',
    width: 240
  },
  {
    title: '状态',
    key: 'status'
  }
]

onMounted(() => {
  const orderSn = route.query.orderSn
  if (!orderSn) {
    state.loading = false
    return
  }

  // 加载订单信息
  fetchOrderBySn({ orderSn })
    .then((res) => {
      if (res.success && res.data) {
        state.order = res.data
      }
    })
    .finally(() => {
      state.loading = false
    })

  fetchOrderStatusLog({ orderSn })
    .then((res) => {
      if (res.success && Array.isArray(res.data)) {
        statusLogs.value = res.data
      }
    })
    .catch(() => {
      statusLogs.value = []
    })

  // 加载退票记录
  refundsLoading.value = true
  fetchRefundByOrderSn({ orderSn })
    .then((res) => {
      if (res.success && res.data) {
        // 支持数组或单条数据
        refundsData.value = Array.isArray(res.data) ? res.data : [res.data]
      }
    })
    .catch((err) => {
      console.warn('获取退票记录失败，将使用订单数据兜底:', err)
      refundsData.value = []
    })
    .finally(() => {
      refundsLoading.value = false
    })
})
</script>

<style lang="scss" scoped>
.order-detail-page {
  max-width: 1100px;
  margin: 0 auto;
}
.breadcrumb-bar {
  margin-bottom: 16px;
  font-size: 14px;
  color: #475569;
  .sep {
    margin: 0 4px;
  }
  a {
    color: #1d4ed8;
  }
}
.detail-tabs {
  :deep(.ant-tabs-nav) {
    margin-bottom: 0;
  }
}
.track-layout {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 16px 0;
}
.order-side-card {
  width: 220px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #1677ff 0%, #0958d9 100%);
  color: #fff;
  border-radius: 12px;
  padding: 20px 16px;
  position: relative;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 12px 100%, 0 calc(100% - 12px));
}
.side-title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  padding-bottom: 10px;
}
.side-row {
  font-size: 13px;
  line-height: 1.8;
  opacity: 0.95;
}
.timeline-panel {
  flex: 1;
  min-width: 280px;
  padding: 8px 12px;
  background: #f8fbff;
  border-radius: 12px;
  border: 1px solid #e4ecf8;
}
.node-date {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
}
.node-time {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}
.node-title {
  color: #1d4ed8;
  font-weight: 600;
  margin-bottom: 6px;
}
.node-title-fail {
  color: #dc2626;
}
.node-line {
  color: #ea580c;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 4px;
}
.refund-list-container {
  margin-bottom: 20px;
}
.refund-card {
  margin-bottom: 20px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;

  &:last-child {
    margin-bottom: 0;
  }
}
.refund-info-cell {
  line-height: 2;
  .label {
    color: #666;
    font-size: 14px;
  }
  .amount-value {
    color: #ff6600;
    font-weight: 600;
    font-size: 16px;
  }
  .service-fee-value {
    color: #ff6600;
    font-weight: 500;
    font-size: 14px;
  }
  .fee-value {
    color: #ff6600;
    font-weight: 500;
    font-size: 14px;
  }
  .source-value {
    color: #52c41a;
    font-weight: 500;
  }
  .time-value {
    color: #ff6600;
    font-size: 14px;
  }
}
.detail-text {
  color: #52c41a;
  font-weight: 500;
  font-size: 14px;
}
.serial-text {
  font-family: 'Courier New', monospace;
  color: #666;
  font-size: 13px;
}
.status-text {
  color: #52c41a;
  line-height: 1.6;
  font-size: 14px;
}
.warm-tip {
  margin-top: 16px;
  border-radius: 12px;
}
.tl-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}
.tl-axis {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 14px;
  flex-shrink: 0;
}
.tl-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1677ff;
  border: 2px solid #bfdbfe;
}
.tl-line {
  flex: 1;
  width: 2px;
  min-height: 24px;
  margin-top: 4px;
  background: #dbeafe;
}
.tl-body {
  flex: 1;
  min-width: 0;
}
</style>
